/**
 * The store, as bars (BR-19, BR-20, BR-21, P2-TR3).
 *
 * A number tells you there are 40 kg of bhusi. A bar against a reorder marker
 * tells you whether 40 kg is a lot or nearly nothing, which is the question
 * somebody standing in the store actually has.
 *
 * The bar is scaled against twice the reorder level rather than against the
 * largest stock on the farm. Scaling to the largest would make a 500 kg sack
 * of bhusi flatten every medicine bar to a sliver, and the comparison that
 * matters is each item against its own threshold, not against each other.
 *
 * This screen was read-only for two phases, which is why it looked unfinished:
 * `POST /inventory`, `/inventory/purchase` and `/inventory/:id/adjust` had all
 * existed since P2-T02 with nothing calling them, so feed bought for the
 * animals could not be put into the store at all. The three writes live here
 * now, each next to the item it concerns rather than on a separate page —
 * somebody standing in the store with a sack in front of them should not have
 * to navigate.
 *
 * Staff read the store and feed out of it (§1.4) but write nothing here: a
 * purchase posts an expense, and a count is the one write that can make the
 * books agree with a wrong number.
 */

import { useState, type FormEvent } from 'react';

import { useTranslation } from '../../i18n/useTranslation.js';
import { errorFields, errorMessageKey } from '../../lib/apiError.js';
import { formatMoney } from '../../lib/currency.js';
import { todayInDhaka } from '../../lib/date.js';
import { useAuth } from '../auth/AuthContext.js';
import { useAccounts, useCategories } from '../finance/api.js';
import { useAdjustStock, useCreateItem, useInventory, useRecordPurchase, type InventoryItem } from './api.js';

/** Digits with at most two decimals, checked as text — never parsed (MONEY-1). */
const DECIMAL_DRAFT = /^\d{1,12}(\.\d{1,2})?$/;

const UNITS = ['kg', 'litre', 'piece', 'dose'] as const;
const ITEM_TYPES = ['feed', 'medicine'] as const;

/** Where the bar fills to, and where the reorder marker sits, as percentages. */
export function barGeometry(item: Pick<InventoryItem, 'currentStock' | 'reorderLevel'>): {
  fillPercent: number;
  markerPercent: number | null;
} {
  const stock = Math.max(0, Number(item.currentStock));
  const reorder = Number(item.reorderLevel);

  // No threshold set: the bar is informational, so it fills against the stock
  // itself and carries no marker.
  if (reorder <= 0) return { fillPercent: stock > 0 ? 100 : 0, markerPercent: null };

  const scale = reorder * 2;
  return {
    fillPercent: Math.min(100, (stock / scale) * 100),
    // Always at the halfway point by construction, which makes "left of the
    // line is trouble" readable at a glance across every row.
    markerPercent: 50,
  };
}

/**
 * What a count would change, worked out for display only.
 *
 * The server does this arithmetic again and its answer is the one that gets
 * written — this exists so a partner sees the consequence before they commit
 * to it, which is the difference between correcting a figure and guessing at
 * one. Returns null while the input is not yet a number.
 */
export function countDifference(countedQty: string, currentStock: string): string | null {
  if (!DECIMAL_DRAFT.test(countedQty.trim())) return null;

  const difference = Number(countedQty) - Number(currentStock);
  // Two decimals, and an explicit sign: "2.00" and "-2.00" answer different
  // questions and must not look alike at a glance.
  return `${difference > 0 ? '+' : ''}${difference.toFixed(2)}`;
}

/** Buying feed into the store (BR-19). Stock is immediate; the money is not. */
function PurchaseForm({ item, onDone }: { item: InventoryItem; onDone: () => void }): JSX.Element {
  const { t } = useTranslation();
  const { data: accounts = [] } = useAccounts();
  const { data: categories = [] } = useCategories();
  const purchase = useRecordPurchase();

  const [qty, setQty] = useState('');
  const [unitPrice, setUnitPrice] = useState('');
  const [date, setDate] = useState(todayInDhaka());
  const [onCredit, setOnCredit] = useState(false);
  const [accountId, setAccountId] = useState<number | null>(null);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const active = accounts.filter((account) => account.isActive);
  const selectedAccount = accountId ?? active[0]?.id ?? null;
  /** The feed category, so the expense lands somewhere a P&L can read. */
  const feedCategory = categories.find((category) => /feed|খাদ্য/i.test(category.name));

  const amounts = DECIMAL_DRAFT.test(qty) && DECIMAL_DRAFT.test(unitPrice);
  const total = amounts ? (Number(qty) * Number(unitPrice)).toFixed(2) : null;
  const canSubmit = amounts && (onCredit || selectedAccount !== null) && !purchase.isPending;

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setErrorKey(null);
    setFieldErrors({});

    try {
      await purchase.mutateAsync({
        itemId: item.id,
        date,
        qty,
        unitPrice,
        // No account on a credit purchase: nothing left an account, so naming
        // one would take money out of a balance nobody spent (P1-BR5).
        ...(onCredit ? {} : { accountId: selectedAccount! }),
        ...(feedCategory !== undefined ? { categoryId: feedCategory.id } : {}),
      });
      onDone();
    } catch (error) {
      setErrorKey(errorMessageKey(error));
      setFieldErrors(errorFields(error) ?? {});
    }
  }

  return (
    <form className="mt-3 rounded-lg bg-gray-50 p-3" onSubmit={(event) => void handleSubmit(event)} noValidate>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="mb-1 block text-xs text-gray-600" htmlFor={`qty-${String(item.id)}`}>
            {t('inventory.buy.qty', { unit: item.unit })}
          </label>
          <input
            id={`qty-${String(item.id)}`}
            inputMode="decimal"
            value={qty}
            onChange={(event) => setQty(event.target.value)}
            placeholder="0"
            className="min-h-touch w-full rounded-lg border border-gray-300 px-2 py-2 text-right tabular-nums"
          />
        </div>

        <div>
          <label className="mb-1 block text-xs text-gray-600" htmlFor={`price-${String(item.id)}`}>
            {t('inventory.buy.unitPrice', { unit: item.unit })}
          </label>
          <input
            id={`price-${String(item.id)}`}
            inputMode="decimal"
            value={unitPrice}
            onChange={(event) => setUnitPrice(event.target.value)}
            placeholder="0.00"
            className="min-h-touch w-full rounded-lg border border-gray-300 px-2 py-2 text-right tabular-nums"
          />
        </div>
      </div>

      {/* The number the partner is actually agreeing to, before they commit. */}
      <p aria-live="polite" className="mt-2 text-right text-sm text-gray-700">
        {total === null ? '—' : t('inventory.buy.total', { amount: formatMoney(total) })}
      </p>

      <label className="mb-1 mt-2 block text-xs text-gray-600" htmlFor={`date-${String(item.id)}`}>
        {t('inventory.buy.date')}
      </label>
      <input
        id={`date-${String(item.id)}`}
        type="date"
        value={date}
        max={todayInDhaka()}
        onChange={(event) => setDate(event.target.value)}
        className="min-h-touch w-full rounded-lg border border-gray-300 px-2 py-2"
      />

      <fieldset className="mt-3">
        <legend className="mb-1 text-xs text-gray-600">{t('inventory.buy.howPaid')}</legend>
        <div className="grid grid-cols-2 gap-2">
          {[false, true].map((credit) => (
            <button
              key={String(credit)}
              type="button"
              onClick={() => setOnCredit(credit)}
              aria-pressed={onCredit === credit}
              className={`min-h-touch rounded-lg px-2 py-2 text-sm ${
                onCredit === credit ? 'bg-green-700 font-medium text-white' : 'bg-white text-gray-700 shadow-sm'
              }`}
            >
              {t(credit ? 'inventory.buy.credit' : 'inventory.buy.cash')}
            </button>
          ))}
        </div>
      </fieldset>

      {onCredit ? (
        <p className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900">{t('inventory.buy.creditNote')}</p>
      ) : (
        <div className="mt-2 flex flex-wrap gap-2">
          {active.map((account) => (
            <button
              key={account.id}
              type="button"
              onClick={() => setAccountId(account.id)}
              aria-pressed={selectedAccount === account.id}
              className={`min-h-touch rounded-full px-3 py-2 text-sm ${
                selectedAccount === account.id
                  ? 'bg-green-700 font-medium text-white'
                  : 'bg-white text-gray-700 shadow-sm'
              }`}
            >
              {account.name}
            </button>
          ))}
        </div>
      )}

      {fieldErrors['accountId'] !== undefined && (
        <p className="mt-1 text-sm text-red-700">{fieldErrors['accountId']}</p>
      )}

      {/* The stock is real now; the money is not yet agreed (BR-07). Saying so
          stops "bought" being read as "paid for and approved". */}
      <p className="mt-2 rounded-lg bg-gray-100 px-3 py-2 text-xs text-gray-600">{t('inventory.buy.pendingNote')}</p>

      {errorKey !== null && (
        <div role="alert" className="mt-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-800">
          <p>{t(errorKey)}</p>
        </div>
      )}

      <div className="mt-3 flex gap-2">
        <button
          type="submit"
          disabled={!canSubmit}
          className="min-h-touch flex-1 rounded-lg bg-green-700 text-sm font-medium text-white disabled:opacity-50"
        >
          {t(purchase.isPending ? 'app.saving' : 'inventory.buy.submit')}
        </button>
        <button type="button" onClick={onDone} className="min-h-touch rounded-lg bg-white px-3 text-sm text-gray-700">
          {t('app.cancel')}
        </button>
      </div>
    </form>
  );
}

/** A physical count (BR-21). The figure is what is there, not the change. */
function CountForm({ item, onDone }: { item: InventoryItem; onDone: () => void }): JSX.Element {
  const { t } = useTranslation();
  const adjust = useAdjustStock();

  const [countedQty, setCountedQty] = useState('');
  const [note, setNote] = useState('');
  const [date, setDate] = useState(todayInDhaka());
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const difference = countDifference(countedQty, item.currentStock);
  // The reason is mandatory on the server, so asking here saves a round trip
  // and an error naming a field the partner has already filled in.
  const canSubmit = difference !== null && note.trim().length >= 3 && !adjust.isPending;

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setErrorKey(null);
    setFieldErrors({});

    try {
      await adjust.mutateAsync({ itemId: item.id, date, countedQty, note: note.trim() });
      onDone();
    } catch (error) {
      setErrorKey(errorMessageKey(error));
      setFieldErrors(errorFields(error) ?? {});
    }
  }

  return (
    <form className="mt-3 rounded-lg bg-gray-50 p-3" onSubmit={(event) => void handleSubmit(event)} noValidate>
      <p className="mb-2 text-xs text-gray-600">
        {t('inventory.count.expected', { qty: item.currentStock, unit: item.unit })}
      </p>

      <label className="mb-1 block text-xs text-gray-600" htmlFor={`counted-${String(item.id)}`}>
        {t('inventory.count.counted', { unit: item.unit })}
      </label>
      <input
        id={`counted-${String(item.id)}`}
        inputMode="decimal"
        value={countedQty}
        onChange={(event) => setCountedQty(event.target.value)}
        placeholder="0"
        className="min-h-touch w-full rounded-lg border border-gray-300 px-2 py-2 text-right tabular-nums"
      />

      {/*
        The consequence, before it is committed. A partner correcting a figure
        should see that they are about to write off 2 kg, not discover it in a
        variance report next week.
      */}
      <p
        aria-live="polite"
        className={`mt-2 text-right text-sm font-medium tabular-nums ${
          difference === null ? 'text-gray-400' : difference.startsWith('-') ? 'text-red-700' : 'text-green-700'
        }`}
      >
        {difference === null ? '—' : t('inventory.count.difference', { difference, unit: item.unit })}
      </p>

      <label className="mb-1 mt-2 block text-xs text-gray-600" htmlFor={`count-date-${String(item.id)}`}>
        {t('inventory.count.date')}
      </label>
      <input
        id={`count-date-${String(item.id)}`}
        type="date"
        value={date}
        max={todayInDhaka()}
        onChange={(event) => setDate(event.target.value)}
        className="min-h-touch w-full rounded-lg border border-gray-300 px-2 py-2"
      />

      <label className="mb-1 mt-2 block text-xs text-gray-600" htmlFor={`note-${String(item.id)}`}>
        {t('inventory.count.reason')}
      </label>
      <input
        id={`note-${String(item.id)}`}
        value={note}
        onChange={(event) => setNote(event.target.value)}
        placeholder={t('inventory.count.reasonHint')}
        className="min-h-touch w-full rounded-lg border border-gray-300 px-2 py-2"
      />
      {fieldErrors['note'] !== undefined && <p className="mt-1 text-sm text-red-700">{fieldErrors['note']}</p>}

      {errorKey !== null && (
        <div role="alert" className="mt-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-800">
          <p>{t(errorKey)}</p>
        </div>
      )}

      <div className="mt-3 flex gap-2">
        <button
          type="submit"
          disabled={!canSubmit}
          className="min-h-touch flex-1 rounded-lg bg-green-700 text-sm font-medium text-white disabled:opacity-50"
        >
          {t(adjust.isPending ? 'app.saving' : 'inventory.count.submit')}
        </button>
        <button type="button" onClick={onDone} className="min-h-touch rounded-lg bg-white px-3 text-sm text-gray-700">
          {t('app.cancel')}
        </button>
      </div>
    </form>
  );
}

function StockBar({ item, canWrite }: { item: InventoryItem; canWrite: boolean }): JSX.Element {
  const { t } = useTranslation();
  const { fillPercent, markerPercent } = barGeometry(item);
  const [open, setOpen] = useState<'buy' | 'count' | null>(null);

  return (
    <li className="rounded-xl bg-white p-3 shadow-sm">
      <div className="flex items-baseline justify-between gap-2">
        <span className="truncate font-medium text-gray-800">{item.name}</span>
        <span
          className={`shrink-0 tabular-nums text-sm ${item.isLow ? 'font-semibold text-amber-800' : 'text-gray-700'}`}
        >
          {item.currentStock} {item.unit}
        </span>
      </div>

      <div className="relative mt-2 h-2.5 overflow-hidden rounded-full bg-gray-100">
        <div
          className={`h-full ${item.isLow ? 'bg-amber-500' : 'bg-green-600'}`}
          style={{ width: `${String(fillPercent)}%` }}
        />
        {markerPercent !== null && (
          <div
            aria-hidden="true"
            className="absolute top-0 h-full w-0.5 bg-gray-500"
            style={{ left: `${String(markerPercent)}%` }}
          />
        )}
      </div>

      <div className="mt-1 flex justify-between text-xs text-gray-500">
        <span>
          {item.isLow
            ? t('inventory.lowAt', { level: item.reorderLevel, unit: item.unit })
            : t(`inventory.type.${item.itemType}`)}
        </span>
        <span className="tabular-nums">{formatMoney(item.stockValue)}</span>
      </div>

      {canWrite && (
        <div className="mt-2 flex gap-2">
          {(['buy', 'count'] as const).map((action) => (
            <button
              key={action}
              type="button"
              onClick={() => setOpen(open === action ? null : action)}
              aria-expanded={open === action}
              className="min-h-touch flex-1 rounded-lg bg-gray-100 px-2 py-2 text-xs font-medium text-gray-700"
            >
              {t(`inventory.${action}.open`)}
            </button>
          ))}
        </div>
      )}

      {open === 'buy' && <PurchaseForm item={item} onDone={() => setOpen(null)} />}
      {open === 'count' && <CountForm item={item} onDone={() => setOpen(null)} />}
    </li>
  );
}

/** Adding something the farm stocks that is not on the list yet (BR-19). */
function AddItemForm({ onDone }: { onDone: () => void }): JSX.Element {
  const { t } = useTranslation();
  const create = useCreateItem();

  const [name, setName] = useState('');
  const [itemType, setItemType] = useState<(typeof ITEM_TYPES)[number]>('feed');
  const [unit, setUnit] = useState<(typeof UNITS)[number]>('kg');
  const [reorderLevel, setReorderLevel] = useState('');
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const canSubmit = name.trim().length >= 2 && !create.isPending;

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setErrorKey(null);
    setFieldErrors({});

    try {
      await create.mutateAsync({
        name: name.trim(),
        itemType,
        unit,
        ...(DECIMAL_DRAFT.test(reorderLevel) ? { reorderLevel } : {}),
      });
      onDone();
    } catch (error) {
      setErrorKey(errorMessageKey(error));
      setFieldErrors(errorFields(error) ?? {});
    }
  }

  return (
    <form className="mb-4 rounded-xl bg-white p-3 shadow-sm" onSubmit={(event) => void handleSubmit(event)} noValidate>
      <label className="mb-1 block text-xs text-gray-600" htmlFor="item-name">
        {t('inventory.add.name')}
      </label>
      <input
        id="item-name"
        value={name}
        onChange={(event) => setName(event.target.value)}
        className="min-h-touch w-full rounded-lg border border-gray-300 px-2 py-2"
      />
      {fieldErrors['name'] !== undefined && <p className="mt-1 text-sm text-red-700">{fieldErrors['name']}</p>}

      <div className="mt-2 grid grid-cols-2 gap-2">
        <div>
          <label className="mb-1 block text-xs text-gray-600" htmlFor="item-type">
            {t('inventory.add.type')}
          </label>
          <select
            id="item-type"
            value={itemType}
            onChange={(event) => setItemType(event.target.value as (typeof ITEM_TYPES)[number])}
            className="min-h-touch w-full rounded-lg border border-gray-300 px-2 py-2"
          >
            {ITEM_TYPES.map((option) => (
              <option key={option} value={option}>
                {t(`inventory.type.${option}`)}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="mb-1 block text-xs text-gray-600" htmlFor="item-unit">
            {t('inventory.add.unit')}
          </label>
          <select
            id="item-unit"
            value={unit}
            onChange={(event) => setUnit(event.target.value as (typeof UNITS)[number])}
            className="min-h-touch w-full rounded-lg border border-gray-300 px-2 py-2"
          >
            {UNITS.map((option) => (
              <option key={option} value={option}>
                {t(`inventory.unit.${option}`)}
              </option>
            ))}
          </select>
        </div>
      </div>
      {/* BR-19 — the unit is fixed once movements exist, so it is worth
          getting right before the first sack is recorded against it. */}
      <p className="mt-1 text-xs text-gray-500">{t('inventory.add.unitHint')}</p>

      <label className="mb-1 mt-2 block text-xs text-gray-600" htmlFor="item-reorder">
        {t('inventory.add.reorderLevel')}
      </label>
      <input
        id="item-reorder"
        inputMode="decimal"
        value={reorderLevel}
        onChange={(event) => setReorderLevel(event.target.value)}
        placeholder="0"
        className="min-h-touch w-full rounded-lg border border-gray-300 px-2 py-2 text-right tabular-nums"
      />
      <p className="mt-1 text-xs text-gray-500">{t('inventory.add.reorderHint')}</p>

      {errorKey !== null && (
        <div role="alert" className="mt-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-800">
          <p>{t(errorKey)}</p>
        </div>
      )}

      <div className="mt-3 flex gap-2">
        <button
          type="submit"
          disabled={!canSubmit}
          className="min-h-touch flex-1 rounded-lg bg-green-700 text-sm font-medium text-white disabled:opacity-50"
        >
          {t(create.isPending ? 'app.saving' : 'inventory.add.submit')}
        </button>
        <button type="button" onClick={onDone} className="min-h-touch rounded-lg bg-white px-3 text-sm text-gray-700">
          {t('app.cancel')}
        </button>
      </div>
    </form>
  );
}

export function InventoryDashboard(): JSX.Element {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { data: items = [], isLoading, isError, refetch } = useInventory();
  const [adding, setAdding] = useState(false);

  // §1.4 — staff feed out of the store but never buy into it or correct it.
  const canWrite = user?.role === 'admin' || user?.role === 'partner';
  const lowCount = items.filter((item) => item.isLow).length;

  return (
    <div className="page-container">
      <div className="flex items-baseline justify-between py-4">
        <h1 className="text-xl font-semibold text-gray-800">{t('inventory.title')}</h1>
        {canWrite && !adding && (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="min-h-touch rounded-lg bg-green-700 px-3 py-2 text-sm font-medium text-white"
          >
            {t('inventory.add.open')}
          </button>
        )}
      </div>

      {adding && <AddItemForm onDone={() => setAdding(false)} />}

      {isLoading && (
        <p role="status" className="py-8 text-center text-gray-500">
          {t('app.loading')}
        </p>
      )}

      {isError && (
        <div role="alert" className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-800">
          <p className="mb-2">{t('error.network')}</p>
          <button type="button" onClick={() => void refetch()} className="min-h-touch font-medium underline">
            {t('app.retry')}
          </button>
        </div>
      )}

      {lowCount > 0 && (
        <p className="mb-4 rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-900">
          {t('inventory.lowCount', { count: lowCount })}
        </p>
      )}

      {items.length === 0 && !isLoading && <p className="py-8 text-center text-gray-500">{t('inventory.empty')}</p>}

      {/* The server already puts what is low first: the list answers "what do
          we need to buy?", and alphabetical makes that a scan. */}
      <ul className="space-y-2">
        {items.map((item) => (
          <StockBar key={item.id} item={item} canWrite={canWrite} />
        ))}
      </ul>
    </div>
  );
}
